import { Link } from 'react-router-dom';
import { Shield, Heart, AlertCircle, BookOpen } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function Terms() {
  const lastUpdated = 'February 2026';

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />

      {/* Hero */}
      <div className="max-w-4xl mx-auto px-6 pt-12 pb-6">
        <div className="flex items-center gap-2 mb-3" style={{ color: '#E57A44' }}>
          <Shield className="w-4 h-4" />
          <span className="text-xs uppercase tracking-widest" style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 700, letterSpacing: '0.15em' }}>Terms of Service</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold mb-3" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
          The Terms of Our Walk Together
        </h1>
        <p className="text-lg" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', lineHeight: 1.6 }}>
          Clear, honest, and written to protect both of us. Love Restored is a place for faith-based encouragement and honest conversation about relationships — here's what that means, and what it doesn't.
        </p>
        <p className="text-sm mt-3" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>Last updated: {lastUpdated}</p>
      </div>

      {/* The Big Disclaimer — most important, up front */}
      <div className="max-w-4xl mx-auto px-6">
        <div className="p-6 rounded-3xl relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #FFF5E5 0%, #FFFBF3 100%)', border: '1px solid #E8C26540', boxShadow: '0 4px 20px rgba(0,0,0,0.03)' }} data-testid="terms-disclaimer">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-xl flex-shrink-0" style={{ backgroundColor: '#E8C26525' }}>
              <AlertCircle className="w-5 h-5" style={{ color: '#B38A2E' }} />
            </div>
            <div>
              <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Please read this carefully</h2>
              <p className="mb-3" style={{ fontFamily: 'Nunito, sans-serif', color: '#4A3D1E', lineHeight: 1.7 }}>
                <strong>Love Restored offers faith-based guidance, biblical encouragement, and pastoral-style consultation — not licensed therapy, counseling, medical advice, or mental-health treatment.</strong>
              </p>
              <p className="mb-3" style={{ fontFamily: 'Nunito, sans-serif', color: '#4A3D1E', lineHeight: 1.7 }}>
                Nothing on this site should replace professional care. If you are experiencing a mental-health crisis, thoughts of harming yourself or others, domestic violence, or a medical emergency, please:
              </p>
              <ul className="space-y-1 mb-2" style={{ fontFamily: 'Nunito, sans-serif', color: '#4A3D1E', lineHeight: 1.7 }}>
                <li>• Call or text <strong>988</strong> (Suicide &amp; Crisis Lifeline, U.S.)</li>
                <li>• Call <strong>911</strong> for immediate emergencies</li>
                <li>• Contact the <strong>National Domestic Violence Hotline</strong> at 1-800-799-7233</li>
                <li>• Reach out to a licensed therapist, counselor, physician, or pastoral care provider</li>
              </ul>
              <p className="mt-3 text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#4A3D1E' }}>
                See our full <Link to="/resources" style={{ color: '#B38A2E', fontWeight: 700, textDecoration: 'underline' }}>Resources page</Link> for a broader directory of hotlines and support lines.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-4xl mx-auto px-6 py-10 space-y-8" style={{ fontFamily: 'Nunito, sans-serif', color: '#3E3B37', lineHeight: 1.8, fontSize: '16px' }}>

        <Section num="1" title="Who we are and what we offer" tint="#E57A44">
          <p>Love Restored is a faith-based website created by <strong>Genesis Natera</strong> that shares:</p>
          <ul className="mt-2 space-y-1 pl-4">
            <li>• Articles grounded in Scripture on marriage, family, dating, friendship, and personal growth</li>
            <li>• A "Feelings" tool that suggests biblical verses and encouragement based on what you're going through</li>
            <li>• A Prayer Wall and anonymous sharing community</li>
            <li>• Optional <strong>paid consultations</strong> — one-on-one conversations offering biblical encouragement, prayer, and general life guidance</li>
          </ul>
          <p className="mt-3">All content and consultations are offered <strong>as spiritual encouragement and general life guidance</strong>. They are not, and should not be interpreted as, professional therapy, psychological counseling, medical advice, or legal advice.</p>
        </Section>

        <Section num="2" title="Consultations — scope of service" tint="#5B7C99">
          <p>When you book a paid consultation with Love Restored:</p>
          <ul className="mt-2 space-y-1 pl-4">
            <li>• You are engaging a <strong>faith-based mentor / life guide</strong>, not a licensed therapist, psychologist, psychiatrist, counselor, social worker, doctor, or attorney.</li>
            <li>• Sessions may include prayer, scripture reflection, active listening, and general encouragement.</li>
            <li>• We <strong>do not diagnose, treat, or provide clinical care</strong> for any mental-health or medical condition.</li>
            <li>• We will refer you to a licensed professional if your situation is beyond the scope of faith-based encouragement.</li>
            <li>• You remain fully responsible for your own decisions and actions taken based on any conversation.</li>
          </ul>
        </Section>

        <Section num="3" title="Payment, refunds, and cancellations" tint="#78A18E">
          <p>Consultations are billed through Stripe at the tier you select (Basic $25, Premium $50, In-Depth $100). Payment is held with Stripe until a service is delivered.</p>
          <p className="mt-2">If we are unable to deliver a response within a reasonable time, or if you are dissatisfied with the response, you may request a refund from your consultation page. Refunds are reviewed case-by-case and, when approved, returned to your original payment method by Stripe.</p>
        </Section>

        <Section num="4" title="Community conduct" tint="#B77BB0">
          <p>To keep this a safe, restoring space, please:</p>
          <ul className="mt-2 space-y-1 pl-4">
            <li>• Be kind. This is a place of healing, not debate.</li>
            <li>• Do not post harassment, hate speech, sexually explicit material, doxxing, or content that harms minors.</li>
            <li>• Do not share the personal information of others without their consent.</li>
            <li>• Do not use the site for spam, promotion, or unsolicited advertising.</li>
          </ul>
          <p className="mt-3">We may remove any content and terminate any account that violates these guidelines, at our sole discretion.</p>
        </Section>

        <Section num="5" title="Your account and privacy" tint="#E8C265">
          <p>You are responsible for keeping your login credentials secure. You may unsubscribe from emails at any time using the link in every email, or by adjusting your preferences on the site.</p>
          <p className="mt-2">Prayer requests and anonymous shares are visible to other users unless you explicitly mark them private. Please do not share information you would not want other members to see.</p>
        </Section>

        <Section num="6" title="Content and intellectual property" tint="#E57A44">
          <p>All articles, images, video, and design on Love Restored are the intellectual property of Genesis Natera and Love Restored unless otherwise noted. You are welcome to share links to our articles freely, but please do not reproduce full articles, videos, or design elsewhere without written permission.</p>
          <p className="mt-2">Scripture quotations, where used, come from public-domain or freely-usable translations and remain the property of their respective publishers.</p>
        </Section>

        <Section num="7" title="Limitation of liability" tint="#5B7C99">
          <p>Love Restored, Genesis Natera, and their affiliates are not liable for any direct, indirect, incidental, or consequential damages arising from your use of this site or its consultations. You use this site and its services at your own risk.</p>
          <p className="mt-2">Nothing in these terms is intended to limit any rights you may have under applicable law that cannot lawfully be limited.</p>
        </Section>

        <Section num="8" title="Changes and contact" tint="#78A18E">
          <p>We may update these terms as the site grows. When we do, we'll update the "Last updated" date at the top and, for meaningful changes, notify subscribed members by email.</p>
          <p className="mt-2">Questions about these terms? <Link to="/contact" style={{ color: '#E57A44', fontWeight: 600 }}>Reach out through our contact form</Link>.</p>
        </Section>

        <div className="p-6 rounded-3xl text-center mt-10" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
          <Heart className="w-6 h-6 mx-auto mb-3" style={{ color: '#E57A44' }} />
          <p className="italic mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29', fontSize: 17 }}>
            "She is clothed with strength and dignity; she can laugh at the days to come."
          </p>
          <p className="text-sm" style={{ color: '#8C867D', fontWeight: 600 }}>Proverbs 31:25</p>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function Section({ num, title, tint, children }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <span className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold" style={{ backgroundColor: `${tint}20`, color: tint, fontFamily: 'Merriweather, serif' }}>
          {num}
        </span>
        <h2 className="text-xl font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{title}</h2>
      </div>
      <div className="pl-12">{children}</div>
    </div>
  );
}
